import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { SummaryCard } from "../components/SummaryCard";
import { MapView } from "../components/MapView";
import { LayerPicker, LAYERS } from "../components/LayerPicker";
import { ControlStack } from "../components/ControlStack";
import { TimelineBar } from "../components/TimelineBar";
import { DetailPanel } from "../components/DetailPanel";
import { MapLegend } from "../components/MapLegend";
import { CompareDialog } from "../components/CompareDialog";
import { DailySummaryDialog } from "../components/DailySummaryDialog";
import { useDailySummary } from "../hooks/useDailySummary";
import { useRadarFrames } from "../hooks/useRadarFrames";
import { useWindGrid, useScalarGrid, useFires } from "../hooks/useWindGrid";
import { getWeather, reverseGeocode, getConfig } from "../lib/api";
import { conditionTheme } from "../lib/weather";
import { SCALAR_LAYERS } from "../lib/colorScales";

const DEFAULT_LOCATION = {
  name: "Jakarta",
  state: "DKI Jakarta",
  country: "ID",
  lat: -6.2088,
  lon: 106.8456,
};

const LS = {
  units: "aethera_units",
  theme: "aethera_theme",
  favorites: "aethera_favorites",
  last: "aethera_last_location",
  rain: "aethera_rain_alerts",
  home: "aethera_home",
  layer: "aethera_layer",
  fireDays: "aethera_fire_days",
  fireVisible: "aethera_fire_visible",
  fireConf: "aethera_fire_conf",
};

const load = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const locKey = (l) => `${l.lat.toFixed(2)},${l.lon.toFixed(2)}`;

export default function Dashboard() {
  const [units, setUnits] = useState(() => localStorage.getItem(LS.units) || "metric");
  const [theme, setTheme] = useState(() => localStorage.getItem(LS.theme) || "dark");
  const [favorites, setFavorites] = useState(() => load(LS.favorites, []));
  const [home, setHome] = useState(() => load(LS.home, null));
  const [location, setLocation] = useState(() => load(LS.last, DEFAULT_LOCATION));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [liveData, setLiveData] = useState(true);
  const [compareOpen, setCompareOpen] = useState(false);
  const [rainAlerts, setRainAlerts] = useState(() => load(LS.rain, false));
  const [picking, setPicking] = useState(null);
  const [layer, setLayer] = useState(() => localStorage.getItem(LS.layer) || "wind");
  const [hourIdx, setHourIdx] = useState(0);
  const [forecastPlaying, setForecastPlaying] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [fireDays, setFireDays] = useState(() => load(LS.fireDays, 1));
  const [firesVisible, setFiresVisible] = useState(() => load(LS.fireVisible, true));
  const [fireConf, setFireConf] = useState(() => load(LS.fireConf, { l: true, n: true, h: true }));
  const mapRef = useRef(null);
  const notified = useRef(new Set());
  const summary = useDailySummary({ home, favorites, current: location, units });
  const radar = useRadarFrames(layer === "radar");
  const { grid: windGridRaw, loading: windLoading, error: windError, retry: windRetry } = useWindGrid(bounds, layer === "wind");
  const isScalar = SCALAR_LAYERS.includes(layer);
  const { fires, loading: firesLoading, error: firesError, retry: firesRetry } = useFires(bounds, fireDays, layer === "wind" && firesVisible);

  useEffect(() => localStorage.setItem(LS.fireDays, JSON.stringify(fireDays)), [fireDays]);
  useEffect(() => localStorage.setItem(LS.fireVisible, JSON.stringify(firesVisible)), [firesVisible]);
  useEffect(() => localStorage.setItem(LS.fireConf, JSON.stringify(fireConf)), [fireConf]);

  const confKey = (c) => (c === "low" || c === "l" ? "l" : c === "high" || c === "h" ? "h" : "n");
  const filteredFires = useMemo(() => {
    if (!fires) return null;
    const points = fires.points.filter((p) => fireConf[confKey(p.confidence)]);
    return { ...fires, points, shown: points.length };
  }, [fires, fireConf]);
  const toggleConf = (k) =>
    setFireConf((c) => {
      const next = { ...c, [k]: !c[k] };
      return Object.values(next).some(Boolean) ? next : c;
    });
  const { grid: scalarGrid, loading: scalarLoading, error: scalarError, retry: scalarRetry } = useScalarGrid(bounds, layer, isScalar);


  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(LS.theme, theme);
  }, [theme]);

  useEffect(() => localStorage.setItem(LS.units, units), [units]);
  useEffect(() => localStorage.setItem(LS.favorites, JSON.stringify(favorites)), [favorites]);
  useEffect(() => localStorage.setItem(LS.layer, layer), [layer]);

  useEffect(() => {
    getConfig().then((c) => setLiveData(c.live_data)).catch(() => {});
  }, []);

  const fetchWeather = useCallback(async (loc, silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const res = await getWeather(loc);
      res.current.__offset = res.location.timezone_offset;
      res.location = { ...res.location, village: loc.village || "", district: loc.district || "", regency: loc.regency || "" };
      setData(res);
      setHourIdx(0);
      setLiveData(res.source === "live");
      setLocation(loc);
      localStorage.setItem(LS.last, JSON.stringify(loc));
    } catch (e) {
      if (silent) throw e;
      setError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const handleMapPick = useCallback(
    async (lat, lon) => {
      setPicking({ lat, lon });
      try {
        const loc = await reverseGeocode(lat, lon);
        await fetchWeather({ ...loc, lat, lon }, true);
        toast.success(`Menampilkan cuaca: ${loc.name}`);
      } catch {
        toast.error("Gagal mengambil cuaca di titik tersebut");
      } finally {
        setPicking(null);
      }
    },
    [fetchWeather]
  );

  const locateMe = useCallback(
    (silent) =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error("unsupported"));
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              const { latitude, longitude } = pos.coords;
              const loc = await reverseGeocode(latitude, longitude);
              await fetchWeather(loc);
              if (!silent) toast.success(`Lokasi ditemukan: ${loc.name}`);
              resolve(loc);
            } catch (e) {
              reject(e);
            }
          },
          reject,
          { timeout: silent ? 6000 : 10000 }
        );
      }),
    [fetchWeather]
  );

  useEffect(() => {
    if (home) {
      fetchWeather(home);
      return;
    }
    locateMe(true).catch(() => fetchWeather(location));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      toast.error("Geolokasi tidak didukung browser ini");
      return;
    }
    setGeoLoading(true);
    locateMe(false)
      .catch((e) => toast.error(e?.code ? "Izin lokasi ditolak" : "Gagal mengambil cuaca lokasi Anda"))
      .finally(() => setGeoLoading(false));
  };

  const toggleFavorite = () => {
    const key = locKey(location);
    if (favorites.some((f) => locKey(f) === key)) {
      setFavorites((f) => f.filter((x) => locKey(x) !== key));
      toast("Dihapus dari favorit");
    } else {
      setFavorites((f) => [...f, location]);
      toast.success(`${location.name} disimpan ke favorit`);
    }
  };

  const isFavorite = favorites.some((f) => locKey(f) === locKey(location));
  const isHome = !!home && locKey(home) === locKey(location);

  const toggleHome = () => {
    if (isHome) {
      setHome(null);
      localStorage.removeItem(LS.home);
      toast("Kota rumah dihapus");
    } else {
      setHome(location);
      localStorage.setItem(LS.home, JSON.stringify(location));
      toast.success(`${location.name} ditetapkan sebagai Kota Rumah`);
    }
  };

  // ---- Rain alerts ----
  const fireRainAlert = useCallback((name, status) => {
    const key = `${name}-${status.dt}`;
    if (notified.current.has(key)) return;
    notified.current.add(key);
    const msg = `${name}: kemungkinan hujan ${status.pop}% dalam ~1 jam ke depan. Siapkan payung!`;
    toast.warning(msg, { duration: 8000 });
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("Peringatan Hujan — KayongWeather", { body: msg });
      } catch {
        /* ignore */
      }
    }
  }, []);

  const rainStatus = (d) => {
    const h = d.hourly && d.hourly[0];
    if (!h) return null;
    const rainy = ["Rain", "Thunderstorm", "Drizzle"].includes(h.condition.main);
    if (rainy || h.pop >= 60) return { pop: h.pop, dt: h.dt };
    return null;
  };

  useEffect(() => {
    if (!rainAlerts || !data) return;
    const cur = rainStatus(data);
    if (cur) fireRainAlert(data.location.name, cur);
    const others = favorites.filter((f) => locKey(f) !== locKey(location));
    Promise.all(others.map((f) => getWeather(f).then((d) => ({ f, d })).catch(() => null))).then((list) =>
      list.filter(Boolean).forEach(({ f, d }) => {
        const st = rainStatus(d);
        if (st) fireRainAlert(f.name, st);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rainAlerts, data, favorites]);

  const toggleRainAlerts = async () => {
    const next = !rainAlerts;
    setRainAlerts(next);
    localStorage.setItem(LS.rain, JSON.stringify(next));
    if (next) {
      if ("Notification" in window && Notification.permission === "default") {
        try {
          await Notification.requestPermission();
        } catch {
          /* ignore */
        }
      }
      toast.success("Notifikasi hujan diaktifkan untuk kota favorit & aktif");
    } else {
      toast("Notifikasi hujan dimatikan");
    }
  };

  // ---- Forecast timeline ----
  const steps = useMemo(() => (data ? [data.current, ...data.hourly] : null), [data]);
  const selected = useMemo(() => {
    if (!data) return null;
    if (hourIdx === 0) return { ...data.current, pop: data.hourly[0]?.pop ?? 0 };
    return data.hourly[hourIdx - 1];
  }, [data, hourIdx]);

  // uniform wind fallback from the active city's forecast when the Open-Meteo grid is unavailable
  const windFallback = layer === "wind" && !windGridRaw && !!windError && !!selected && !!bounds;
  const windGrid = useMemo(() => {
    if (!windFallback) return windGridRaw;
    const sp = selected.wind_speed || 0;
    const rad = ((selected.wind_deg || 0) * Math.PI) / 180;
    const u = -sp * Math.sin(rad), v = -sp * Math.cos(rad);
    const pad = 2;
    return {
      nx: 2, ny: 2,
      lats: [bounds.latMin - pad, bounds.latMax + pad],
      lons: [bounds.lonMin - pad, bounds.lonMax + pad],
      t0: selected.dt, step: 3600, hours: 1,
      u: [[u], [u], [u], [u]], v: [[v], [v], [v], [v]],
    };
  }, [windFallback, windGridRaw, selected, bounds]);

  useEffect(() => {
    if (!forecastPlaying || !steps) return;
    const t = setInterval(() => setHourIdx((i) => (i + 1) % steps.length), 1200);
    return () => clearInterval(t);
  }, [forecastPlaying, steps]);

  const theme_ = selected
    ? conditionTheme(selected.condition.main, selected.condition.icon)
    : { accent: "#4F46E5", glow: "rgba(79,70,229,0.22)" };

  const activeLayer = LAYERS.find((l) => l.key === layer);

  return (
    <div className="min-h-screen" style={{ "--accent-color": theme_.accent }}>
      <MapView
        mapRef={mapRef}
        location={data ? data.location : location}
        theme={theme}
        liveData={liveData}
        layer={layer}
        radar={radar}
        windGrid={windGrid}
        windTs={selected ? selected.dt : Math.floor(Date.now() / 1000)}
        scalarGrid={scalarGrid}
        fires={filteredFires}
        firesVisible={firesVisible}
        onPick={handleMapPick}
        onBounds={setBounds}
        picking={picking}
      />

      <SummaryCard
        data={data}
        selected={selected}
        isNow={hourIdx === 0}
        units={units}
        isFavorite={isFavorite}
        onToggleFavorite={toggleFavorite}
        isHome={isHome}
        onToggleHome={toggleHome}
        onOpenDetail={() => setPanelOpen(true)}
        favorites={favorites}
        home={home}
        homeKey={home ? locKey(home) : null}
        activeKey={locKey(location)}
        onSelectCity={fetchWeather}
        onRemoveFav={(f) => setFavorites((fs) => fs.filter((x) => locKey(x) !== locKey(f)))}
        liveData={liveData}
        loading={loading}
        error={error}
        onRetry={() => fetchWeather(location)}
      />

      <LayerPicker layer={layer} onChange={setLayer} />

      <MapLegend
        layer={layer}
        grid={scalarGrid}
        scalar={{ loading: scalarLoading, error: scalarError, retry: scalarRetry }}
        wind={{ grid: windGrid, loading: windLoading, error: windError, retry: windRetry, fallback: windFallback }}
        fire={{ fires: filteredFires, conf: fireConf, toggleConf, days: fireDays, setDays: setFireDays, visible: firesVisible, setVisible: setFiresVisible, loading: firesLoading, error: firesError, retry: firesRetry }}
        units={units}
      />

      <ControlStack
        mapRef={mapRef}
        units={units}
        onToggleUnits={() => setUnits((u) => (u === "metric" ? "imperial" : "metric"))}
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        onGeolocate={handleGeolocate}
        geoLoading={geoLoading}
        onCompare={() => setCompareOpen(true)}
        rainAlerts={rainAlerts}
        onToggleRainAlerts={toggleRainAlerts}
        dailySummaryEnabled={summary.settings.enabled}
        onDailySummary={() => summary.setOpen(true)}
      />

      {picking && (
        <div className="fixed inset-x-0 top-4 z-30 flex justify-center pointer-events-none" data-testid="map-pick-loading">
          <div className="glass rounded-full px-4 py-2 text-xs font-semibold shadow-lg">Mengambil cuaca di titik yang dipilih...</div>
        </div>
      )}

      {steps && (
        <TimelineBar
          steps={steps}
          offset={data.location.timezone_offset}
          hourIdx={hourIdx}
          onChange={(i) => {
            setForecastPlaying(false);
            setHourIdx(i);
          }}
          playing={forecastPlaying}
          onTogglePlay={() => setForecastPlaying((p) => !p)}
          layer={layer}
          radar={radar}
        />
      )}

      <div className="windy-attrib" data-testid="map-attribution">
        {activeLayer?.label} · {activeLayer?.source} · Peta Esri · Klik peta untuk lihat cuaca
      </div>

      <DetailPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        data={data}
        units={units}
        accent={theme_.accent}
        liveData={liveData}
      />

      <CompareDialog open={compareOpen} onOpenChange={setCompareOpen} current={location} favorites={favorites} units={units} />
      <DailySummaryDialog summary={summary} units={units} />
    </div>
  );
}
